import { Injectable, inject, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, NavigationStart } from '@angular/router';
import { filter } from 'rxjs/operators';

export interface VersionInfo {
  buildId: string;
  builtAt?: string;
}

@Injectable({
  providedIn: 'root'
})
export class VersionCheckService {
  private platformId = inject(PLATFORM_ID);
  private router = inject(Router);

  private currentBuildId: string | null = null;
  private isChecking = false;
  private checkIntervalTimer: any = null;

  public readonly showUpdateModal = signal<boolean>(false);
  public readonly updateMessage = signal<string>(
    'We have just updated the website with new improvements. We are sorry for any inconvenience—please reload the page to continue.'
  );

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.init();
    }
  }

  private async init(): Promise<void> {
    // 1. Fetch initial version
    try {
      const initial = await this.fetchVersion();
      if (initial?.buildId) {
        this.currentBuildId = initial.buildId;
      }
    } catch {
      // Ignore initial fetch errors if any
    }

    // 2. Check whenever user returns to the tab
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.checkVersion();
        }
      });

      window.addEventListener('focus', () => {
        this.checkVersion();
      });
    }

    // 3. Check before route transitions
    this.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe(() => {
        this.checkVersion();
      });

    // 4. Periodic check every 10 minutes
    this.checkIntervalTimer = setInterval(() => {
      this.checkVersion();
    }, 10 * 60 * 1000);
  }

  public async checkVersion(): Promise<boolean> {
    if (!isPlatformBrowser(this.platformId)) return false;
    if (this.isChecking || this.showUpdateModal() || !this.currentBuildId) return false;

    this.isChecking = true;
    try {
      const serverVersion = await this.fetchVersion();
      if (serverVersion?.buildId && serverVersion.buildId !== this.currentBuildId) {
        this.triggerUpdateModal();
        return true;
      }
    } catch {
      // Server might be temporarily unreachable, ignore
    } finally {
      this.isChecking = false;
    }

    return false;
  }

  private async fetchVersion(): Promise<VersionInfo | null> {
    try {
      const res = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache'
        }
      });
      if (res.ok) {
        return (await res.json()) as VersionInfo;
      }
    } catch {
      // Network failure or offline
    }
    return null;
  }

  public triggerUpdateModal(customMessage?: string): void {
    if (customMessage) {
      this.updateMessage.set(customMessage);
    }
    this.showUpdateModal.set(true);
  }

  public reload(): void {
    if (isPlatformBrowser(this.platformId)) {
      // Force reload from server
      window.location.reload();
    }
  }
}

